import { Box, Button, Checkbox, Input, Popup, Table, TopBar } from '@sam/components';
import { TableHeader, TableKey, TableRow } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  InventoryCheck,
  InventoryEntry,
  InventoryGroup,
  Language,
  addOrRemove,
  downloadFile,
  getInventoryOverviewCsv,
  updateInventoryEntry,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { useUser } from '../../components/UserContext';
import {
  convertInventoryEntriesIntoTableEntries,
  generateEmptyInventoryCheck,
} from '../../utils/inventory/Inventory.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

const InventoryOverview: React.FC<{ group: InventoryGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation<{ selectedCategories: string[] }>();
  const { axios, updateContextUser, user } = useUser();
  const { data: allOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [], revalidateOnMount: true },
  });
  const [includeDeactivated, toggleIncludeDeactivated] = useState<boolean>(false);
  const { data: inventoryEntries, mutate } = useData('INVENTORY_ENTRIES_GROUP', {
    config: { fallbackData: [] },
    params: { group, includeDeactivated },
  });
  const { data: inventoryCategories } = useData('INVENTORY_CATEGORY_GROUP', {
    config: { fallbackData: [] },
    params: { group },
  });
  const { data: simpleUsers } = useData('SIMPLEUSERS_ALL', {
    config: { fallbackData: [] },
  });

  const [filterValue, setFilterValue] = useState<string>('');

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    location.state?.selectedCategories || [],
  );
  const [entryToAddCheck, setEntryToAddCheck] = useState<InventoryEntry | undefined>();
  const [checkToAdd, setcheckToAdd] = useState<InventoryCheck>(
    generateEmptyInventoryCheck(user.id),
  );

  //Hook to select all categories on load if no categories where passed on
  useEffect(() => {
    if (location.state?.selectedCategories) return;
    setSelectedCategories(inventoryCategories.map((cat) => cat.id));
  }, [inventoryCategories, location.state?.selectedCategories]);
  /**
   * Helper method to crete and download the overview csv
   */
  const handleCsvDownload = (): void => {
    getInventoryOverviewCsv(
      axios,
      user?.language || Language.DE,
      selectedCategories,
      includeDeactivated,
    ).then(
      (generatedFile) =>
        generatedFile && downloadFile(generatedFile, t('pages.inventory.overviewFilename')),
    );
  };

  /**
   * Holds the actual tableRows for the entries
   */
  const rows: TableRow[] = useMemo((): TableRow[] => {
    const filteredEntries: InventoryEntry[] = inventoryEntries.filter((entry) =>
      selectedCategories.includes(entry.categoryId),
    );
    return convertInventoryEntriesIntoTableEntries(
      filteredEntries,
      inventoryCategories,
      navigate,
      allOffices,
      simpleUsers,
      selectedCategories,
      setEntryToAddCheck,
    );
  }, [
    allOffices,
    inventoryCategories,
    inventoryEntries,
    navigate,
    selectedCategories,
    simpleUsers,
  ]);

  /**
   * Holds the correct tableKey for the inventoryOverviews
   */
  const tableKey: TableKey = useMemo(
    (): TableKey =>
      group === InventoryGroup.CONTRACT
        ? TableKey.INVENTORY_OVERVIEW_CONTRACT
        : group === InventoryGroup.ITEM
          ? TableKey.INVENTORY_OVERVIEW_ITEM
          : TableKey.INVENTORY_OVERVIEW_TOOL,
    [group],
  );
  return (
    <>
      {entryToAddCheck && (
        <Popup
          isOpen={!!entryToAddCheck}
          onClose={() => setEntryToAddCheck(undefined)}
          title={t('pages.inventory.addCheck')}
          buttons={[
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => setEntryToAddCheck(undefined)}
            />,
            <Button
              value={t('general.buttons.add')}
              onClick={() => {
                updateInventoryEntry(axios, {
                  ...entryToAddCheck,
                  checks: [...entryToAddCheck.checks, checkToAdd],
                }).then(() => {
                  setEntryToAddCheck(undefined);
                  mutate();
                });
              }}
            />,
          ]}
        >
          <>
            <Input type="date" value={checkToAdd?.createDate} disabled />
            <Input
              type="text"
              label={t('pages.inventory.comment')}
              onChange={(comment) => setcheckToAdd({ ...checkToAdd, comment })}
              value={checkToAdd.comment}
            />
            <Checkbox
              isChecked={checkToAdd.passed}
              onCheck={(passed) => setcheckToAdd({ ...checkToAdd, passed })}
              label={t('pages.inventory.success')}
            />
          </>
        </Popup>
      )}
      <TopBar title={t(`pages.inventory.title${group}`)} onSearch={setFilterValue}>
        <IconComponent
          type={IconType.ADD}
          onClick={() =>
            navigate('/inventory/edit', {
              state: { group, selectedCategories },
            })
          }
        />
        <IconComponent
          type={IconType.DISABLED}
          style={{ color: includeDeactivated ? '#bc2e46' : '' }}
          onClick={() => toggleIncludeDeactivated(!includeDeactivated)}
        />
        <IconComponent
          type={IconType.DOWNLOAD}
          width={20}
          height={20}
          onClick={handleCsvDownload}
        />
      </TopBar>
      <Box>
        <div className="three-columns">
          {inventoryCategories.map((category) => (
            <Button
              active={selectedCategories.includes(category.id)}
              value={category.translations.get(user.language || Language.DE) || category.title}
              onClick={() => setSelectedCategories(addOrRemove(selectedCategories, category.id))}
            />
          ))}
        </div>
        <Table
          tableConfig={user.tableConfig?.get(tableKey)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(axios, user, tableKey, updatedHeader).then(
              (updatedUser) => updatedUser && updateContextUser(updatedUser),
            );
          }}
          filterValue={filterValue}
          header={
            t(`pages.inventory.overviewTable${group}`, {
              returnObjects: true,
            }) as TableHeader[]
          }
          rows={rows}
        />
      </Box>
    </>
  );
};

export default InventoryOverview;
